// Copied from the nowadays repo, packages/ui/src/lib/utils.ts at 6da58398f. Change it there first, then copy it here, rewriting any em dash (this repo bans them).
// One change: TEXT_STYLES carries the `ui-` prefix the frontend gives the type scale.
import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * The composed text styles from @nowadays/design-tokens.
 *
 * They have to be declared here because they read as `text-*`, which is also
 * how every text color reads. Left unregistered, the merge guesses "color"
 * and drops the real color sitting beside them, so a component that asks for
 * a size silently loses its color and inherits whatever its parent had.
 *
 * They get their own group rather than joining `font-size`, because each one
 * bundles size *and* weight *and* leading *and* tracking. Treated as a size
 * alone, a primitive's `font-normal` sits happily alongside and still wins the
 * weight, which is how the crumb for the current page ended up lighter than
 * the ancestor linking back to it.
 */
const TEXT_STYLES = [
  "ui-h1",
  "ui-h2",
  "ui-h3",
  "ui-h4",
  "ui-h5",
  "ui-h6",
  "ui-subtitle-1",
  "ui-subtitle-2",
  "ui-body-1",
  "ui-body-2",
  "ui-caption",
  "ui-overline",
];

/**
 * The named elevation scale from @nowadays/design-tokens.
 *
 * Declared for the same reason as the text styles: tailwind-merge does not
 * know them, so it read `shadow-raised` as a shadow *color*. Then a later
 * `shadow-none` did not replace it, and the stylesheet's order decided which
 * one won, so an override on a card silently lost. Joining the stock `shadow`
 * group makes them what they are: a shadow, replaced by the next one.
 */
const ELEVATION_LEVELS = ["control", "raised", "overlay", "modal"];

/** Declared to tailwind-merge so it can be referenced as a conflict target. */
type TextStyleGroup = "nw-text-style";

const twMerge = extendTailwindMerge<TextStyleGroup>({
  extend: {
    classGroups: {
      "nw-text-style": [{ text: TEXT_STYLES }],
      shadow: [{ shadow: ELEVATION_LEVELS }],
    },
    // Everything the composed utility sets itself, so anything hand-set before
    // it is dropped rather than left to fight it on stylesheet order. A raw
    // utility written *after* still wins, which keeps a deliberate one-off
    // override possible.
    conflictingClassGroups: {
      "nw-text-style": ["font-size", "font-weight", "leading", "tracking"],
      "font-size": ["nw-text-style"],
    },
  },
});

/** Merge class names, resolving conflicting Tailwind utilities (last wins). */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
